import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AUTEUR_LIA, AUTEUR_SYSTEME, EVENEMENT_FICHE_CREEE, EVENEMENT_FICHE_FICHIER, MESSAGE_TYPE_CONVERSATION, changementDuSuivi, phraseDesFichesLiees, sorteDuMessageDeSuivi } from '@taki/data-model';
export { chatterActifSurLeJeu } from '@taki/data-model';
import type { CatalogueDuSuivi, Message } from '@taki/data-model';
import type { TakiSdk } from '@taki/sdk';
import { projectRecord, useLiaEnAttente, usePersonnesPort, useSuivisLia } from '@taki/ui';
import type { Annuaires, ObservateurDeSuivisLia } from '@taki/ui';
import type {
  ActiviteDuSuivi,
  ChangementDuSuivi,
  DetailDuCompte,
  EntreeDuSuivi,
  PersonneDuSuivi,
  PersonneVue,
  SuiviDuGabarit,
} from '@taki/ui';

/**
 * LE SUIVI DE LA FICHE AFFICHEE, vu du gabarit (chantier `chatter` du
 * 05/10/2026, devenu « suivi » le 27/10/2026, chantier `fiche-suivi`). Le
 * contrat : `docs/socle/fiche-suivi.md`, section 1, et `docs/socle/chatter.md`.
 *
 * CE QUE CE MODULE FAIT : il nomme la fiche affichee par une ANCRE, lit son
 * suivi par le SDK (la conversation `chatter`, son fil en temps reel, le total
 * serveur, les activites ouvertes), le met en mots pour le gabarit, et ecrit ce
 * que le volet permet : un message, MONTRE DES L'ENVOI (`fusionnerLesEnvois`,
 * puis confirme ou en echec a renvoyer), une activite faite, l'epingle du suivi
 * dans la fenetre Conversations. Le suivi nait au premier geste
 * (`ouvrirChatter`) quand le serveur ne l'a pas deja cree.
 *
 * LES NOMS DE DONNEES NE CHANGENT PAS : la conversation reste de type
 * `chatter`, sa clef `fiche:<famille>/<id>`, les evenements du shell
 * `taki:chatter:*` (le centre de communication les ecoute) ; seul ce qu'on LIT
 * s'appelle « suivi ». Renommer une donnee aurait demande une migration de
 * chaque org pour un gain nul a l'ecran.
 *
 * LE SOCLE PEUT ETRE ABSENT : sans `chatterDe` sur le SDK, le rail se rend
 * quand meme, eteint, avec la raison (G16).
 */

/**
 * L'EVENEMENT QUI OUVRE LE CENTRE, jumeau de `EVENEMENT_OUVRIR_CENTRE` dans
 * `packages/shell/src/centre/ouvrirCentre.ts`. Recopie et non importe :
 * `@taki/app-runtime` ne depend pas de `@taki/shell`.
 */
export const EVENEMENT_OUVRIR_CENTRE = 'taki:centre:ouvrir';

/**
 * L'EVENEMENT QUE LE CENTRE EMET QUAND UN MESSAGE PART dans un suivi
 * (`detail: { cle }`) : le compte se relit. Le volet de la fiche, lui, suit le
 * fil en temps reel et n'en a pas besoin.
 */
export const EVENEMENT_CHATTER_ECRIT = 'taki:chatter:ecrit';

/** L'ancre d'un suivi telle que le gabarit la connait : la fiche, et de quoi la nommer. */
export type AncreDuSuivi =
  | { family: string; recordId: string; appId: string; doorId: string; titre: string }
  | { appId: string; doorId: string; titre: string }
  | { appId: string; titre: string };

/** L'ancre telle que le SDK la lit (`AncreDeConversation`, stricte). */
export type AncreDuSocle = { family: string; recordId: string } | { appId: string; doorId?: string };

export function ancreDuSocle(ancre: AncreDuSuivi): AncreDuSocle {
  if ('family' in ancre) return { family: ancre.family, recordId: ancre.recordId };
  if ('doorId' in ancre) return { appId: ancre.appId, doorId: ancre.doorId };
  return { appId: ancre.appId };
}

/** La cle de l'ancre, la meme que `cleDeChatter` du socle : `fiche:...`, `porte:...`, `app:...`. */
export function cleDAncre(ancre: AncreDuSuivi): string {
  if ('family' in ancre) return `fiche:${ancre.family}/${ancre.recordId}`;
  if ('doorId' in ancre) return `porte:${ancre.appId}/${ancre.doorId}`;
  return `app:${ancre.appId}`;
}

/**
 * LE TITRE D'UNE FICHE, TEL QU'ELLE L'AFFICHE, pour nommer son suivi (le bouton
 * du rail, le centre de messages, les notifications). C'est le titre de la
 * rangee de titre de la Fiche (`projectRecord` avec les annuaires de la porte :
 * un titre-REFERENCE se lit par le nom de la fiche designee, un titre a
 * catalogue par son libelle), jamais un identifiant : un titre-reference que
 * l'annuaire ne nomme pas (pas encore charge, fiche designee disparue) rendrait
 * l'id brut, et un titre vide rendrait l'id de la fiche ; dans les deux cas on
 * rend `''`, et `libelleDuSuivi` dit alors « Suivi de la fiche » tout court.
 */
export function titreAffichableDeLaFiche(
  record: unknown,
  id: string,
  binding: Parameters<typeof projectRecord>[2],
  annuaires?: Annuaires,
): string {
  const titre = binding?.roles?.['title'];
  if (titre === undefined) return '';
  const affiche = projectRecord(record, id, binding, false, annuaires).title;
  if (affiche === '' || affiche === id) return '';
  const designe = titre.parts?.some((partie) => partie.reference !== undefined) === true;
  if (!designe) return affiche;
  /* UN TITRE-REFERENCE NON NOMME rend la valeur stockee (l'id de la fiche
     designee) : on la compare a ce que le champ porte, partie par partie. */
  const brutes = (titre.parts ?? []).filter((partie) => partie.reference !== undefined).map((partie) => {
    const valeur = partie.get(record);
    return valeur === null || valeur === undefined ? '' : String(valeur);
  });
  return brutes.some((brute) => brute !== '' && affiche.includes(brute)) ? '' : affiche;
}

/**
 * L'ANCRE DU SUIVI DE LA FICHE OUVERTE, ou `null` : la seule regle de l'ecran
 * (`EcranDeclare`), ecrite en fonction pure pour qu'un test rejoue la sequence
 * d'une fiche qu'on vient de creer.
 *
 * UNE FICHE QU'ON N'A PAS ENCORE LUE N'A PAS D'ANCRE (28/10/2026). Juste apres
 * « Creer », l'id est dans l'adresse avant que la valeur de la fiche arrive (la
 * lecture de la fiche creee, `useScreen`, prend de quelques centaines de
 * millisecondes a plusieurs secondes quand un modele a cree des fiches liees) :
 * l'ancre naissait alors avec un titre vide, et le rail disait « Suivi de la
 * fiche » sans le nom de l'animal alors que la fiche, une fois lue, le nomme
 * (« Suivi de la fiche Rex »). Le titre se recalcule bien a chaque rendu, mais
 * un bouton sans nom est un bouton qui ment pendant tout ce temps. Pas de
 * valeur, pas d'ancre : le rail apparait avec son nom, au meme instant que la
 * fiche. `surfaceSansLecture` : une porte CANVAS tient sa propre surface, qui
 * n'attend pas la valeur du moteur ; elle garde son bouton, titre vide si le
 * moteur ne sait pas encore nommer la fiche.
 */
export function ancreDeLaFicheOuverte(entree: {
  ficheOuverte: string | null;
  /** La valeur de la fiche ouverte (`screen.openRecordValue`), `undefined` tant qu'elle n'est pas lue. */
  record: unknown;
  surfaceSansLecture: boolean;
  /** Le jeu de la porte, ou `null` ; `suiviActif` : sa base ne l'a pas eteint. */
  jeu: { family: string } | null;
  suiviActif: boolean;
  appId: string;
  doorId: string;
  binding: Parameters<typeof projectRecord>[2];
  annuaires?: Annuaires;
}): AncreDuSuivi | null {
  const { ficheOuverte, record, jeu } = entree;
  if (ficheOuverte === null || jeu === null || !entree.suiviActif) return null;
  const lue = record !== undefined && record !== null;
  if (!lue && !entree.surfaceSansLecture) return null;
  const titre = lue ? titreAffichableDeLaFiche(record, ficheOuverte, entree.binding, entree.annuaires) : '';
  return { family: jeu.family, recordId: ficheOuverte, appId: entree.appId, doorId: entree.doorId, titre };
}

/** Le `title` du rail ou du bouton : ce qu'il ouvre. */
export function libelleDuSuivi(ancre: AncreDuSuivi): string {
  if ('family' in ancre) return ancre.titre === '' ? 'Suivi de la fiche' : `Suivi de la fiche ${ancre.titre}`;
  if ('doorId' in ancre) return `Suivi de la porte ${ancre.titre}`;
  return `Suivi de l'app ${ancre.titre}`;
}

/** Ouvre le centre de communication sur « Ici », pour cette ancre (hors fiche). */
export function ouvrirLeCentreSurIci(ancre: AncreDuSuivi): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent(EVENEMENT_OUVRIR_CENTRE, { detail: { source: 'ici', ancre } }),
  );
}

/* ------------------------------------------------------------------ */
/* Le port : ce que le module demande au SDK                           */
/* ------------------------------------------------------------------ */

/** Ce que ce module demande au socle. Le SDK le satisfait (`portDuSuivi`) ; un test, un faux. */
export interface PortDuSuivi extends ObservateurDeSuivisLia {
  chatterDe(ancre: AncreDuSocle): Promise<{ id: string } | null>;
  /** Cree le suivi s'il n'existe pas (le premier message), sinon le rend. */
  ouvrirChatter(ancre: AncreDuSocle): Promise<{ id: string }>;
  compterMessages(conversationId: string): Promise<number>;
  compterLeSuivi(conversationId: string): Promise<DetailDuCompte>;
  /**
   * La fenetre du fil en temps reel. `meta.serveur` : le serveur a vu ce rendu
   * (ni cache, ni ecriture de ce client en attente, le critere de la pastille
   * du SDK, `instantaneDuServeur`) ; le cercle ne se recompte qu'apres lui.
   */
  observer(
    conversationId: string,
    limite: number,
    onChange: (messages: readonly Message[], meta: { serveur: boolean }) => void,
    onError: (erreur: unknown) => void,
  ): () => void;
  /**
   * LES ACTIVITES OUVERTES du suivi, en temps reel, hors de la fenetre du fil
   * (`sdk.messages.observerActivitesOuvertes`). Rend le desabonnement.
   * `meta.fiable` : le rendu dit la base telle que ce client la voit (le
   * serveur, ou sa propre ecriture) ; une remise a zero (session, erreur) ou
   * une lecture du cache, non : une activite qui en est absente n'est pas
   * faite pour autant.
   */
  observerActivitesOuvertes(
    conversationId: string,
    onChange: (activites: readonly Message[], meta: { fiable: boolean }) => void,
    onError: (erreur: unknown) => void,
  ): () => void;
  /** Un id de message neuf (`sdk.messages.nouvelId`), pour montrer le message avant son ecriture. */
  nouvelId?(): string;
  /** `options.id` : l'id du message, choisi par l'appelant (le meme au renvoi). */
  envoyer(conversationId: string, texte: string, options?: { id?: string }): Promise<void>;
  /**
   * L'EPINGLE DU SUIVI dans la fenetre Conversations, PROPRE A LA PERSONNE
   * (`sdk.conversations.preferences`). Les trois ensemble, ou aucun. Une ref :
   * `conversation:<conversationId>`.
   */
  observerEpingles?(onChange: (refs: readonly string[]) => void, onError: (erreur: unknown) => void): () => void;
  epingler?(ref: string): Promise<void>;
  desepingler?(ref: string): Promise<void>;
  marquerFaite(messageId: string, faite: boolean): Promise<void>;
}

export const RAISON_SOCLE_ABSENT =
  "le socle conversations n'est pas monte (sdk.conversations.chatterDe absent)";

/** La page du fil a l'ouverture, puis son pas quand on remonte. */
export const PAGE_DU_SUIVI = 50;
/** Le plus loin qu'on remonte en temps reel (`CONVERSATION_FENETRE_MAX` du SDK). */
export const FENETRE_DU_SUIVI_MAX = 100;

/**
 * Le port, tire du SDK a l'execution : `chatterDe` n'existe que sur la branche
 * du socle conversations. `null` tant qu'il n'y est pas.
 */
export function portDuSuivi(sdk: TakiSdk): PortDuSuivi | null {
  const conversations = sdk.conversations as unknown as {
    chatterDe?: (ancre: AncreDuSocle) => Promise<{ id: string } | null>;
    ouvrirChatter?: (ancre: AncreDuSocle) => Promise<{ id: string }>;
    compterLeSuivi?: (conversationId: string) => Promise<DetailDuCompte & { total: number }>;
    preferences?: {
      observerEpinglees?: (onChange: (refs: readonly string[]) => void, onError?: (erreur: unknown) => void) => () => void;
      epingler?: (ref: never) => Promise<void>;
      desepingler?: (ref: never) => Promise<void>;
    };
  };
  if (typeof conversations.chatterDe !== 'function') return null;
  const messages = sdk.messages as unknown as Partial<TakiSdk['messages']> & TakiSdk['messages'];
  const preferences = conversations.preferences;
  const epingles = typeof preferences?.observerEpinglees === 'function'
    && typeof preferences.epingler === 'function'
    && typeof preferences.desepingler === 'function'
    ? {
        observerEpingles: (onChange: (refs: readonly string[]) => void, onError: (erreur: unknown) => void) => preferences.observerEpinglees!(onChange, onError),
        epingler: (ref: string) => preferences.epingler!(ref as never),
        desepingler: (ref: string) => preferences.desepingler!(ref as never),
      }
    : {};
  return {
    ...epingles,
    ...(typeof messages.nouvelId === 'function' ? { nouvelId: () => messages.nouvelId() } : {}),
    chatterDe: (ancre) => conversations.chatterDe!(ancre),
    ouvrirChatter: (ancre) => {
      if (typeof conversations.ouvrirChatter !== 'function') return Promise.reject(new Error(RAISON_SOCLE_ABSENT));
      return conversations.ouvrirChatter(ancre);
    },
    // Le `type` toujours : sans lui, les rules refusent le compte a un membre regle par type (`rangDeFiche`).
    compterMessages: (conversationId) => sdk.messages.count({ type: MESSAGE_TYPE_CONVERSATION, acces: { conversation: conversationId } }),
    compterLeSuivi: async (conversationId) => {
      if (typeof conversations.compterLeSuivi !== 'function') {
        throw new Error('le socle ne decompose pas le suivi (sdk.conversations.compterLeSuivi absent)');
      }
      const { messages: m, changements, evenements } = await conversations.compterLeSuivi(conversationId);
      return { messages: m, changements, evenements };
    },
    /* Un SDK d'avant `meta.serveur` dit au moins l'ecriture en attente
       (`meta.local`) ; un faux de test sans meta : un rendu du serveur. */
    observer: (conversationId, limite, onChange, onError) =>
      messages.observerConversation(
        conversationId,
        { limite },
        (lus, meta) => onChange(lus, { serveur: meta?.serveur ?? meta?.local !== true }),
        onError,
      ),
    /* Un SDK d'avant la requete des activites ouvertes (un faux de test) : rien
       a ecouter, la fenetre du fil porte seule les epingles, comme avant. */
    observerActivitesOuvertes: (conversationId, onChange, onError) =>
      typeof messages.observerActivitesOuvertes === 'function'
        ? messages.observerActivitesOuvertes(
            conversationId,
            (lues, meta) => onChange(lues, { fiable: meta === undefined || meta.local || meta.serveur !== false }),
            onError,
          )
        : () => undefined,
    /* Un SDK d'avant « Lia au travail » (un faux de test) : pas de suivi, la regle d'age suffit. */
    ...(typeof messages.observerSuivisLia === 'function'
      ? { observerSuivisLia: (conversationId, onChange, onError) => messages.observerSuivisLia(conversationId, onChange, onError) }
      : {}),
    envoyer: async (conversationId, texte, options) => {
      await messages.envoyer(conversationId, texte, options?.id === undefined ? undefined : { id: options.id });
    },
    marquerFaite: (messageId, faite) => messages.marquerFaite(messageId, faite),
  };
}

/** Le total d'un suivi (`0` quand il n'existe pas encore), ou la raison de l'echec. */
export async function lireLeSuivi(
  port: Pick<PortDuSuivi, 'chatterDe' | 'compterMessages'>,
  ancre: AncreDuSuivi,
): Promise<{ compte: number; conversationId: string | null } | { raison: string }> {
  try {
    const conversation = await port.chatterDe(ancreDuSocle(ancre));
    if (conversation === null) return { compte: 0, conversationId: null };
    return { compte: await port.compterMessages(conversation.id), conversationId: conversation.id };
  } catch (cause) {
    return { raison: `le suivi n'a pas pu être lu (${messageDe(cause)})` };
  }
}

const messageDe = (cause: unknown): string => (cause instanceof Error ? cause.message : String(cause));

/* ------------------------------------------------------------------ */
/* Le fil mis en mots : fonctions pures                                */
/* ------------------------------------------------------------------ */

/** L'annuaire du suivi : les personnes par `personId`. */
export type AnnuaireDuSuivi = ReadonlyMap<string, Pick<PersonneVue, 'personId' | 'displayName' | 'email' | 'photoURL'>>;

/**
 * UNE PERSONNE DU SUIVI, resolue par l'annuaire : son nom, sa photo. Lia et le
 * systeme ont leur nom ; un personId inconnu (une personne retiree de
 * l'annuaire) se dit « Inconnu », jamais par son identifiant.
 */
export function personneDuSuivi(id: string, annuaire: AnnuaireDuSuivi): PersonneDuSuivi {
  if (id === AUTEUR_LIA) return { id, nom: 'Lia' };
  if (id === AUTEUR_SYSTEME) return { id, nom: 'Système' };
  const personne = annuaire.get(id);
  if (personne === undefined) return { id, nom: 'Inconnu' };
  const nom = personne.displayName.trim() !== '' ? personne.displayName.trim() : personne.email;
  return { id, nom, ...(personne.photoURL ? { photo: personne.photoURL } : {}) };
}

/**
 * UN CHANGEMENT EN MOTS, PAR LA SEULE REGLE DU JOURNAL (`changementDuSuivi`,
 * `packages/data-model/src/suivi-mots.ts`, lot Q) : la meme que le serveur
 * applique en ecrivant. Un message neuf est deja en mots et ressort tel quel ;
 * un ANCIEN message (ecrit avec des clefs, « Status : aFaire ») se remet en
 * mots par le catalogue de la fiche ; une personne se nomme par l'annuaire.
 */
function changementDuVolet(
  changement: { champ: string; libelle: string; avant: string | null; apres: string | null },
  catalogue: CatalogueDuSuivi,
  annuaire: AnnuaireDuSuivi,
): ChangementDuSuivi {
  const nomDePersonne = (personId: string): string | undefined => {
    const personne = annuaire.get(personId);
    return personne === undefined ? undefined : personne.displayName.trim() || personne.email;
  };
  const { libelle, avant, apres } = changementDuSuivi(changement, catalogue, { nomDePersonne });
  return { libelle, avant, apres };
}

/**
 * LES GESTES DU JOURNAL EN PHRASE, apres le nom de qui les a faits (le gabarit
 * pose la bulle et le nom devant : « Celia a créé la fiche »). Le code de
 * l'evenement (`systeme.evenement`) est un CONTRAT du serveur
 * (`functions/src/chatter/evenements.ts`) ; `systeme.details`, lui, n'est
 * qu'une trace pour la machine (`creation`, `archivedAt`, `files/<id>`) et ne
 * se montre JAMAIS : c'est lui qui affichait « Celia creation » (recette du
 * 27/10). Un evenement que cette table ne connait pas (une fiche liee creee,
 * un code a venir) se dit par son texte, deja en mots par le serveur ; un
 * LOT de fiches liees (`systeme.lot`) se dit par son compte.
 */
const PHRASES_DU_JOURNAL: Readonly<Record<string, string>> = {
  [EVENEMENT_FICHE_CREEE]: 'a créé la fiche',
  'fiche.archivee': 'a archivé la fiche',
  'fiche.restauree': 'a restauré la fiche',
  'fiche.supprimee': 'a supprimé la fiche',
};

/**
 * LE TEXTE D'UN EVENEMENT DU JOURNAL. La phrase ne vaut QUE quand on sait qui
 * a fait le geste (`par`) : un ancien evenement sans `par` se lirait
 * « Compte inconnu a créé la fiche », alors que son texte (« Fiche créée, par
 * Celia ») le dit deja. Sans phrase : le texte du message, jamais `details`.
 */
export function texteDuJournal(
  message: Pick<Message, 'texte' | 'body' | 'systeme'>,
  parConnu: boolean,
): string {
  const systeme = message.systeme;
  const texte = message.texte ?? message.body ?? '';
  if (!parConnu || systeme == null) return texte;
  if (systeme.evenement === EVENEMENT_FICHE_FICHIER && systeme.fichier !== undefined) return `a joint ${systeme.fichier.nom}`;
  /* UN LOT DE FICHES LIEES (deux ou plus) : « a créé 12 fiches liées (Soins) ». Une seule : son texte, comme avant. */
  const lot = phraseDesFichesLiees(systeme);
  if (lot !== null) return lot;
  return PHRASES_DU_JOURNAL[systeme.evenement] ?? texte;
}

/** L'instant d'un message, quelle que soit sa forme (Date, Timestamp, texte). */
function instantDe(valeur: unknown): Date {
  if (valeur instanceof Date) return valeur;
  if (valeur !== null && typeof valeur === 'object' && typeof (valeur as { toDate?: unknown }).toDate === 'function') {
    return (valeur as { toDate(): Date }).toDate();
  }
  if (typeof valeur === 'string' || typeof valeur === 'number') return new Date(valeur);
  return new Date(0);
}

/** Une activite prevue, telle que le gabarit l'epingle. */
function activiteDuMessage(
  message: Message,
  activite: NonNullable<Message['activite']>,
  annuaire: AnnuaireDuSuivi,
): ActiviteDuSuivi {
  return {
    id: String(message.id ?? ''),
    libelle: activite.libelle,
    ...(activite.echeance === undefined ? {} : { echeance: activite.echeance }),
    ...(activite.assigne === undefined ? {} : { assigne: personneDuSuivi(activite.assigne, annuaire) }),
    faite: activite.faiteAt != null,
  };
}

/**
 * LES EPINGLES DU VOLET : les activites de la fenetre du fil, plus les
 * activites OUVERTES que la requete a part a trouvees hors de cette fenetre.
 *
 * - FUSION PAR ID, la fenetre l'emporte : elle lit l'activite elle-meme, et
 *   une ANCIENNE activite, nee avant que la clef `activite.faiteAt` soit posee
 *   a la naissance, n'est trouvee QUE par elle (l'egalite `faiteAt == null` ne
 *   voit pas une clef absente). Rien n'est migre.
 * - FAITE PENDANT LA SESSION, ELLE RESTE, BARREE (recette du 27/10) : hors de
 *   la fenetre, la requete des ouvertes ne rend plus une activite qu'on vient
 *   de faire (ici, ou par Lia, `mark_activity_done`), et la carte disparaissait
 *   au lieu de se barrer. `vuesOuvertes` sont les activites que cette requete a
 *   rendues depuis l'ouverture du suivi : celle qui n'y est plus est passee
 *   d'ouverte a faite, et se lit barree, comme dans la fenetre. Rouverte, la
 *   requete la rend de nouveau, et elle se relit ouverte.
 * - L'ORDRE RESTE CELUI DU FIL, du plus ancien au plus recent : les activites
 *   hors fenetre sont plus anciennes que toute la fenetre, elles passent
 *   devant.
 */
export function activitesEpinglees(
  fenetre: readonly ActiviteDuSuivi[],
  ouvertes: readonly Message[],
  annuaire: AnnuaireDuSuivi,
  vuesOuvertes: readonly Message[] = [],
): ActiviteDuSuivi[] {
  const idDe = (message: Message): string => String(message.id ?? '');
  const dansLaFenetre = new Set(fenetre.map((activite) => activite.id));
  const encoreOuvertes = new Set(ouvertes.map(idDe));
  const horsFenetre = [
    ...ouvertes.map((message) => ({ message, faite: false })),
    ...vuesOuvertes.filter((message) => !encoreOuvertes.has(idDe(message))).map((message) => ({ message, faite: true })),
  ];
  const avant = horsFenetre
    .filter(({ message }) => message.activite != null && !dansLaFenetre.has(idDe(message)))
    .sort((a, b) => instantDe(a.message.createdAt).getTime() - instantDe(b.message.createdAt).getTime())
    .map(({ message, faite }) => {
      const activite = activiteDuMessage(message, message.activite!, annuaire);
      return faite ? { ...activite, faite: true } : activite;
    });
  return avant.length === 0 ? [...fenetre] : [...avant, ...fenetre];
}

/**
 * LES ACTIVITES QUE LA REQUETE DES OUVERTES A RENDUES depuis l'ouverture du
 * suivi, la derniere lecture de chacune l'emportant : la memoire de
 * `activitesEpinglees`. Pure : l'ancienne liste n'est pas modifiee.
 */
export function retenirLesOuvertes(vues: readonly Message[], ouvertes: readonly Message[]): Message[] {
  const parId = new Map(vues.map((message) => [String(message.id ?? ''), message]));
  for (const message of ouvertes) parId.set(String(message.id ?? ''), message);
  return [...parId.values()];
}

/**
 * LE FIL EN ENTREES DU GABARIT, du plus ancien au plus recent, et les activites
 * prevues a part (epinglees, `faite` vraie apres « Fait »).
 *
 * - un message ecrit (une personne, Lia) : `message`, son auteur resolu ;
 * - un evenement `fiche.modifiee` : `changement`, ses champs structures en mots
 *   de la declaration ; un ANCIEN evenement sans `changements` garde son texte,
 *   rien n'est migre ;
 * - tout autre evenement systeme : `evenement`, sa phrase (`texteDuJournal`)
 *   et sa piece jointe ;
 * - une activite prevue : dans `activites`, pas dans le fil.
 *
 * Une reponse dans un fil (`parentId`) n'a pas de place a part dans le volet :
 * elle s'y lit comme un message, a son heure.
 */
export function entreesDuSuivi(
  messages: readonly Message[],
  annuaire: AnnuaireDuSuivi,
  catalogue: CatalogueDuSuivi,
): { entrees: EntreeDuSuivi[]; activites: ActiviteDuSuivi[] } {
  const entrees: EntreeDuSuivi[] = [];
  const activites: ActiviteDuSuivi[] = [];
  const tries = [...messages].sort((a, b) => instantDe(a.createdAt).getTime() - instantDe(b.createdAt).getTime());
  for (const message of tries) {
    const id = String(message.id ?? '');
    const at = instantDe(message.createdAt);
    const sorte = sorteDuMessageDeSuivi(message);
    if (sorte === 'activite' && message.activite != null) {
      activites.push(activiteDuMessage(message, message.activite, annuaire));
      continue;
    }
    const systeme = message.systeme;
    const par = systeme?.par === undefined ? null : personneDuSuivi(systeme.par, annuaire);
    if (sorte === 'changement') {
      const changements = (systeme?.changements ?? []).map((changement) => changementDuVolet(changement, catalogue, annuaire));
      entrees.push({
        sorte: 'changement',
        id,
        par,
        at,
        changements,
        ...(changements.length === 0 ? { texte: texteDuJournal(message, false) } : {}),
      });
      continue;
    }
    if (sorte === 'evenement') {
      const fichier = systeme?.fichier;
      entrees.push({
        sorte: 'evenement',
        id,
        par,
        at,
        texte: texteDuJournal(message, par !== null),
        ...(fichier === undefined || systeme?.evenement !== EVENEMENT_FICHE_FICHIER
          ? {}
          : { fichier: { nom: fichier.nom, ...(fichier.taille === undefined ? {} : { taille: fichier.taille }) } }),
      });
      continue;
    }
    entrees.push({
      sorte: 'message',
      id,
      auteur: personneDuSuivi(message.auteur ?? '', annuaire),
      at,
      texte: message.texte ?? message.body ?? '',
    });
  }
  return { entrees, activites };
}

/**
 * LES CONTRIBUTEURS DU FIL CHARGE, une bulle par personne, la DERNIERE a avoir
 * contribue en tete : l'auteur d'un message, ou qui a fait un changement ou un
 * evenement. Le systeme n'est pas une personne : il n'a pas de bulle. Quand on
 * envoie, sa propre bulle remonte, parce que son message est le plus recent.
 */
export function contributeursDuSuivi(
  entrees: readonly EntreeDuSuivi[],
): (PersonneDuSuivi & { derniere: Date })[] {
  const parPersonne = new Map<string, PersonneDuSuivi & { derniere: Date }>();
  for (const entree of entrees) {
    const personne = entree.sorte === 'message' ? entree.auteur : entree.par;
    if (personne === null || personne.id === AUTEUR_SYSTEME || personne.id === '') continue;
    const connue = parPersonne.get(personne.id);
    if (connue === undefined || connue.derniere.getTime() <= entree.at.getTime()) {
      parPersonne.set(personne.id, { ...personne, derniere: entree.at });
    }
  }
  return [...parPersonne.values()].sort((a, b) => b.derniere.getTime() - a.derniere.getTime());
}

/**
 * UN MESSAGE ENVOYE PAR CE POSTE, pas encore confirme par la base : il est dans
 * le fil des l'appui sur Entree, sous l'id qu'il aura (`nouvelId`). `echec` :
 * l'ecriture a ete refusee, il reste affiche avec sa raison jusqu'au renvoi ou
 * a l'abandon.
 */
export interface EnvoiEnCours {
  id: string;
  /** La cle de la fiche : un envoi ne survit pas a un changement de fiche. */
  cle: string;
  texte: string;
  at: Date;
  etat: 'envoi' | 'echec';
  raison?: string;
}

/**
 * LES ENVOIS EN COURS DANS LE FIL. Un envoi dont l'id est deja dans le fil
 * (l'ecriture locale du SDK) marque cette entree `enAttente` tant qu'il part,
 * `echec` s'il a echoue ; un envoi absent du fil (l'ecriture n'est pas encore
 * partie, ou elle a ete refusee) ajoute son entree, a la suite. Pure.
 */
export function fusionnerLesEnvois(
  entrees: readonly EntreeDuSuivi[],
  envois: readonly EnvoiEnCours[],
  auteur: PersonneDuSuivi,
): EntreeDuSuivi[] {
  if (envois.length === 0) return [...entrees];
  const parId = new Map(envois.map((envoi) => [envoi.id, envoi]));
  const dansLeFil = new Set<string>();
  const fusion = entrees.map((entree): EntreeDuSuivi => {
    const envoi = entree.sorte === 'message' ? parId.get(entree.id) : undefined;
    if (envoi === undefined || entree.sorte !== 'message') return entree;
    dansLeFil.add(envoi.id);
    return envoi.etat === 'envoi' ? { ...entree, enAttente: true } : { ...entree, echec: envoi.raison ?? '' };
  });
  for (const envoi of envois) {
    if (dansLeFil.has(envoi.id)) continue;
    fusion.push({
      sorte: 'message',
      id: envoi.id,
      auteur,
      at: envoi.at,
      texte: envoi.texte,
      enAttente: envoi.etat === 'envoi',
      ...(envoi.etat === 'echec' ? { echec: envoi.raison ?? '' } : {}),
    });
  }
  return fusion;
}

/**
 * LA RAISON D'UN ENVOI REFUSE, EN MOTS DE PERSONNE : la bulle dit « Non envoyé :
 * <raison> ». Le message d'une erreur du SDK nomme des identifiants et des
 * fonctions (« messages.create : la conversation c-... ») : il part en console,
 * jamais a l'ecran. Seules les erreurs ecrites pour une personne passent
 * (le suivi eteint, G16).
 */
export function raisonDeLEnvoi(cause: unknown): string {
  const erreur = cause as { name?: unknown; code?: unknown; message?: unknown } | null;
  if (erreur?.name === 'ChatterEteint' && typeof erreur.message === 'string') return erreur.message;
  if (erreur?.code === 'permission-denied') return 'ce compte ne peut pas écrire ici';
  return "le message n'est pas parti";
}

/** Un id de message neuf quand le SDK n'en fournit pas (un faux de test) : 20 caracteres alphanumeriques. */
function idAleatoire(): string {
  const lettres = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let id = '';
  for (let i = 0; i < 20; i += 1) id += lettres[Math.floor(Math.random() * lettres.length)];
  return id;
}

/**
 * LA DERNIERE ENTREE D'UNE PAGE DU FIL, en signal : l'id de la plus recente
 * (`createdAt`), `''` pour une page vide (une conversation creee sans entree
 * est un etat, pas une absence). C'est ce qui relance le `count()` du cercle.
 */
export function derniereEntreeDuFil(messages: readonly Message[]): string {
  let plusRecente: Message | undefined;
  for (const message of messages) {
    if (plusRecente === undefined || instantDe(message.createdAt).getTime() >= instantDe(plusRecente.createdAt).getTime()) {
      plusRecente = message;
    }
  }
  return plusRecente === undefined ? '' : String(plusRecente.id ?? '');
}

/* ------------------------------------------------------------------ */
/* Le hook du gabarit                                                  */
/* ------------------------------------------------------------------ */

export interface OptionsDuSuivi {
  /**
   * Le fil entier (niveau FICHE : rail et volet). Faux : le seul compte et
   * l'ouverture du centre (le bouton « Suivi » d'une App Canvas).
   */
  fil?: boolean;
  /** Le catalogue de la declaration pour le jeu de la fiche (`catalogueDuSuivi` du data-model). */
  catalogue?: CatalogueDuSuivi;
  /** Vrai : la personne observe sans ecrire (lecteur), le volet n'a pas de saisie. */
  lectureSeule?: boolean;
}

const CATALOGUE_VIDE: CatalogueDuSuivi = new Map();

interface EtatLu {
  cle: string;
  compte: number | null;
  conversationId: string | null;
  raison: string | null;
}

/**
 * LE HOOK DU GABARIT : la prop `suivi` de `ScreenLayout` pour cette ancre.
 * `null` sans ancre (un brouillon, un modele : pas de fiche en base).
 */
export function useSuiviDuGabarit(
  sdk: TakiSdk,
  ancreDemandee: AncreDuSuivi | null,
  options: OptionsDuSuivi = {},
): SuiviDuGabarit | null {
  // On ne suit QUE une fiche (R9 du 06/10/2026) : une ancre de porte ou d'app
  // ne donne rien (le socle la refuse : `ChatterHorsFiche`).
  const ancre = ancreDemandee !== null && 'family' in ancreDemandee ? ancreDemandee : null;
  const cle = ancre === null ? null : cleDAncre(ancre);
  const ancreRef = useRef(ancre);
  ancreRef.current = ancre;
  const fil = options.fil !== false;
  const catalogue = options.catalogue ?? CATALOGUE_VIDE;
  const port = useMemo(() => portDuSuivi(sdk), [sdk]);
  const [lu, setLu] = useState<EtatLu>({ cle: '', compte: null, conversationId: null, raison: null });
  const [tour, setTour] = useState(0);

  /* 1. LA CONVERSATION ET SON TOTAL : une lecture par fiche, une de plus quand
        le centre dit qu'un message est parti. */
  useEffect(() => {
    if (cle === null || ancreRef.current === null || port === null) return;
    let vivant = true;
    void lireLeSuivi(port, ancreRef.current).then((resultat) => {
      if (!vivant) return;
      setLu('compte' in resultat
        ? { cle, compte: resultat.compte, conversationId: resultat.conversationId, raison: null }
        : { cle, compte: null, conversationId: null, raison: resultat.raison });
    });
    return () => { vivant = false; };
  }, [cle, port, tour]);

  useEffect(() => {
    if (cle === null) return;
    const relire = (event: Event): void => {
      const detail = (event as CustomEvent<{ cle?: string } | undefined>).detail;
      if (detail?.cle === undefined || detail.cle === cle) setTour((n) => n + 1);
    };
    window.addEventListener(EVENEMENT_CHATTER_ECRIT, relire);
    return () => window.removeEventListener(EVENEMENT_CHATTER_ECRIT, relire);
  }, [cle]);

  const courant = lu.cle === cle ? lu : null;
  const conversationId = courant?.conversationId ?? null;

  /* 2. LE FIL EN TEMPS REEL, au niveau fiche seulement : la page recente, puis
        plus loin quand le volet remonte. */
  const [limite, setLimite] = useState(PAGE_DU_SUIVI);
  const [fenetre, setFenetre] = useState<{ conversationId: string; messages: readonly Message[]; serveur: boolean } | null>(null);
  const [erreurDuFil, setErreurDuFil] = useState<string | null>(null);
  useEffect(() => {
    setLimite(PAGE_DU_SUIVI);
    setFenetre(null);
    setErreurDuFil(null);
  }, [cle]);
  useEffect(() => {
    if (!fil || port === null || conversationId === null) return;
    return port.observer(
      conversationId,
      limite,
      (messages, { serveur }) => {
        setFenetre({ conversationId, messages, serveur });
        setErreurDuFil(null);
      },
      (erreur) => setErreurDuFil(`le suivi n'a pas pu être lu (${messageDe(erreur)})`),
    );
  }, [fil, port, conversationId, limite]);
  const fenetreCourante = fenetre !== null && fenetre.conversationId === conversationId ? fenetre : null;
  const messages = fenetreCourante?.messages ?? null;

  /* LES ACTIVITES OUVERTES, ecoutees a part de la fenetre : une activite plus
     ancienne que les 50 (ou 100) derniers messages reste epinglee. Un echec de
     cette ecoute ne coupe pas le fil : la fenetre garde ses epingles, et la
     console dit pourquoi. */
  const [ouvertes, setOuvertes] = useState<{
    conversationId: string;
    activites: readonly Message[];
    /** Ce que la requete a rendu depuis l'ouverture (`retenirLesOuvertes`). */
    vues: readonly Message[];
    /** Le dernier rendu est fiable (`meta.fiable`) : son absence vaut « faite ». */
    fiable: boolean;
  } | null>(null);
  useEffect(() => {
    if (!fil || port === null || conversationId === null) return;
    return port.observerActivitesOuvertes(
      conversationId,
      (activites, { fiable }) => setOuvertes((avant) => ({
        conversationId,
        activites,
        vues: retenirLesOuvertes(avant?.conversationId === conversationId ? avant.vues : [], activites),
        fiable,
      })),
      (erreur) => console.warn("[taki/app-runtime] Les activites ouvertes du suivi n'ont pas pu etre lues.", erreur),
    );
  }, [fil, port, conversationId]);
  const ouvertesCourantes = ouvertes !== null && ouvertes.conversationId === conversationId ? ouvertes : null;

  /* LE TOTAL SUIT LE FIL : une entree arrivee (un message envoye ici ou
     ailleurs, un changement ecrit par le serveur) refait le `count()`. Jamais
     sur ce qui est en memoire : la page n'est pas le fil.

     LE SIGNAL EST LA DERNIERE ENTREE, PAS LA LONGUEUR DE LA PAGE (recette du
     27/10) : la fenetre temps reel est bornee (`limite`), donc des qu'elle est
     pleine une entree neuve en pousse une ancienne dehors et la longueur ne
     bouge plus. Le cercle restait fige au-dela de 50 entrees. Remonter le fil
     (`plusAnciennes`) agrandit la page sans changer la derniere entree : pas
     de `count()` pour rien.

     ET IL NE SE LIT QUE SUR UN RENDU DU SERVEUR (recette du 27/10, le cercle
     fige apres le PREMIER message) : le SDK estime `createdAt` dans le rendu
     local de l'envoi, la derniere entree change des l'ecriture locale, et le
     `count()` partait avant que le serveur ait le message. La confirmation,
     elle, ne change pas l'id : rien ne recomptait. Le critere est celui de la
     pastille du SDK (`meta.serveur`) : la derniere entree retenue est celle du
     dernier rendu SERVEUR compte, et le compte part quand un rendu serveur en
     montre une autre. Le premier rendu serveur d'une conversation lue par
     `lireLeSuivi` ne recompte pas ce qui vient d'etre compte. */
  const derniere = messages === null ? null : derniereEntreeDuFil(messages);
  const serveurDuFil = fenetreCourante?.serveur === true;
  const compteeA = useRef<{ conversationId: string; derniere: string } | null>(null);
  useEffect(() => {
    if (port === null || conversationId === null || derniere === null || cle === null || !serveurDuFil) return;
    const avant = compteeA.current;
    compteeA.current = { conversationId, derniere };
    if (avant === null || avant.conversationId !== conversationId || avant.derniere === derniere) return;
    let vivant = true;
    void port.compterMessages(conversationId).then(
      (compte) => { if (vivant) setLu((avant) => (avant.cle === cle ? { ...avant, compte } : avant)); },
      () => undefined,
    );
    return () => { vivant = false; };
  }, [port, conversationId, derniere, serveurDuFil, cle]);

  /* 3. L'ANNUAIRE, lu une fois : les auteurs et `par` deviennent des bulles. */
  const personnes = usePersonnesPort();
  const [annuaire, setAnnuaire] = useState<AnnuaireDuSuivi>(new Map());
  useEffect(() => {
    if (!fil || personnes === null || cle === null) return;
    let vivant = true;
    void personnes.lister().then(
      (liste) => { if (vivant) setAnnuaire(new Map(liste.map((personne) => [personne.personId, personne]))); },
      () => undefined,
    );
    return () => { vivant = false; };
  }, [fil, personnes, cle]);

  const moi = personnes?.moi ?? null;
  const auteurDesEnvois = useMemo<PersonneDuSuivi>(
    () => (moi === null
      ? { id: '', nom: 'Vous' }
      : personneDuSuivi(moi.personId, annuaire.has(moi.personId) ? annuaire : new Map([...annuaire, [moi.personId, moi]]))),
    [moi, annuaire],
  );
  const misEnMots = useMemo(
    () => (messages === null ? null : entreesDuSuivi(messages, annuaire, catalogue)),
    [messages, annuaire, catalogue],
  );
  const epingles = useMemo(
    () => activitesEpinglees(
      misEnMots?.activites ?? [],
      ouvertesCourantes?.activites ?? [],
      annuaire,
      /* Un rendu de remise a zero ne barre rien : les epingles hors fenetre
         s'absentent le temps de la relecture, comme avant. */
      ouvertesCourantes?.fiable === true ? ouvertesCourantes.vues : [],
    ),
    [misEnMots, ouvertesCourantes, annuaire],
  );

  /* LA DECOMPOSITION, lue la premiere fois qu'on la demande, puis gardee pour
     cette conversation ET ce total : un changement ecrit par le serveur (le
     total bouge) la rend perimee, elle se relit. */
  const compteDuSuivi = courant?.compte ?? null;
  const detailRef = useRef<{ conversationId: string; compte: number | null; promesse: Promise<DetailDuCompte> } | null>(null);
  const detail = useCallback((): Promise<DetailDuCompte> => {
    if (port === null) return Promise.reject(new Error(RAISON_SOCLE_ABSENT));
    if (conversationId === null) return Promise.resolve({ messages: 0, changements: 0, evenements: 0 });
    if (detailRef.current?.conversationId !== conversationId || detailRef.current.compte !== compteDuSuivi) {
      detailRef.current = { conversationId, compte: compteDuSuivi, promesse: port.compterLeSuivi(conversationId) };
    }
    return detailRef.current.promesse;
  }, [port, conversationId, compteDuSuivi]);

  /* LE SUIVI NAIT AU PREMIER GESTE (message, epingle) quand le serveur ne l'a
     pas deja cree (base dont le journal ne s'ecrit pas encore, fiche d'avant) :
     `ouvrirChatter` le rend s'il existe. */
  const assurerLeSuivi = useCallback(async (ancreDuGeste: AncreDuSuivi, cleDuGeste: string, existant: string | null): Promise<string> => {
    if (port === null) throw new Error(RAISON_SOCLE_ABSENT);
    if (existant !== null) return existant;
    const cree = (await port.ouvrirChatter(ancreDuSocle(ancreDuGeste))).id;
    /* LE SUIVI NAIT ICI, VIDE ET COMPTE ZERO : son premier rendu serveur
       porte deja ce message, et doit recompter (le cercle passe a 1). */
    compteeA.current = { conversationId: cree, derniere: '' };
    setLu((avant) => (avant.cle === cleDuGeste ? { ...avant, conversationId: cree, compte: avant.compte ?? 0 } : avant));
    return cree;
  }, [port]);

  /* LES ENVOIS EN COURS : le message est dans le fil des l'appel (`etat:
     'envoi'`), l'ecriture se joue ensuite ; confirme, l'envoi disparait (le fil
     du SDK porte le message), refuse, il reste avec sa raison (`echec`). */
  const [envois, setEnvois] = useState<readonly EnvoiEnCours[]>([]);
  useEffect(() => {
    setEnvois((avant) => (avant.some((envoi) => envoi.cle !== cle) ? avant.filter((envoi) => envoi.cle === cle) : avant));
  }, [cle]);
  const jouerLEnvoi = useCallback(async (
    envoi: Pick<EnvoiEnCours, 'id' | 'texte'>,
    ancreDeLEnvoi: AncreDuSuivi,
    cleDeLEnvoi: string,
    existant: string | null,
  ): Promise<void> => {
    try {
      const id = await assurerLeSuivi(ancreDeLEnvoi, cleDeLEnvoi, existant);
      await port!.envoyer(id, envoi.texte, { id: envoi.id });
      detailRef.current = null;
      setEnvois((avant) => avant.filter((autre) => autre.id !== envoi.id));
    } catch (cause) {
      console.warn("[taki/app-runtime] Le message du suivi n'est pas parti.", cause);
      const raison = raisonDeLEnvoi(cause);
      setEnvois((avant) => avant.map((autre) => (autre.id === envoi.id ? { ...autre, etat: 'echec', raison } : autre)));
    }
  }, [port, assurerLeSuivi]);

  const envoyer = useCallback((texte: string): Promise<void> => {
    const ancreCourante = ancreRef.current;
    if (port === null || ancreCourante === null || cle === null) return Promise.reject(new Error(RAISON_SOCLE_ABSENT));
    const id = port.nouvelId?.() ?? idAleatoire();
    setEnvois((avant) => [...avant, { id, cle, texte, at: new Date(), etat: 'envoi' }]);
    return jouerLEnvoi({ id, texte }, ancreCourante, cle, conversationId);
  }, [port, cle, conversationId, jouerLEnvoi]);

  const renvoyer = useCallback((entreeId: string): void => {
    const ancreCourante = ancreRef.current;
    const envoi = envois.find((autre) => autre.id === entreeId && autre.etat === 'echec');
    if (envoi === undefined || port === null || ancreCourante === null || cle === null || envoi.cle !== cle) return;
    setEnvois((avant) => avant.map((autre) => {
      if (autre.id !== entreeId) return autre;
      const { raison: _raison, ...sans } = autre;
      return { ...sans, etat: 'envoi' };
    }));
    void jouerLEnvoi(envoi, ancreCourante, cle, conversationId);
  }, [envois, port, cle, conversationId, jouerLEnvoi]);

  const abandonner = useCallback((entreeId: string): void => {
    setEnvois((avant) => avant.filter((autre) => autre.id !== entreeId));
  }, []);

  const envoisDeLaFiche = useMemo(() => envois.filter((envoi) => envoi.cle === cle), [envois, cle]);
  const entrees = useMemo(
    () => fusionnerLesEnvois(misEnMots?.entrees ?? [], envoisDeLaFiche, auteurDesEnvois),
    [misEnMots, envoisDeLaFiche, auteurDesEnvois],
  );

  /* L'EPINGLE DU SUIVI, par personne : la ref `conversation:<id>` est dans les
     preferences de la personne (ce que la fenetre Conversations observe aussi). */
  const peutEpingler = fil && port !== null && port.observerEpingles !== undefined
    && port.epingler !== undefined && port.desepingler !== undefined;
  const [epinglees, setEpinglees] = useState<readonly string[] | null>(null);
  const aUneCle = cle !== null;
  useEffect(() => {
    if (!peutEpingler || port === null || !aUneCle) return;
    return port.observerEpingles!(
      (refs) => setEpinglees(refs),
      (erreur) => console.warn("[taki/app-runtime] Les epingles de conversations n'ont pas pu etre lues.", erreur),
    );
  }, [peutEpingler, port, aUneCle]);
  const refDuSuivi = conversationId === null ? null : `conversation:${conversationId}`;
  const suiviEpingle = refDuSuivi !== null && epinglees !== null && epinglees.includes(refDuSuivi);
  const basculerLEpingle = useCallback(async (): Promise<void> => {
    const ancreCourante = ancreRef.current;
    if (port === null || ancreCourante === null || cle === null || port.epingler === undefined || port.desepingler === undefined) {
      throw new Error(RAISON_SOCLE_ABSENT);
    }
    const id = await assurerLeSuivi(ancreCourante, cle, conversationId);
    const ref = `conversation:${id}`;
    if (epinglees?.includes(ref) === true) await port.desepingler(ref); else await port.epingler(ref);
  }, [port, cle, conversationId, epinglees, assurerLeSuivi]);

  const marquerFaite = useCallback(
    async (activiteId: string, faite: boolean): Promise<void> => {
      if (port === null) throw new Error(RAISON_SOCLE_ABSENT);
      await port.marquerFaite(activiteId, faite);
    },
    [port],
  );

  const plusAnciennes = useCallback(() => {
    setLimite((avant) => Math.min(FENETRE_DU_SUIVI_MAX, avant + PAGE_DU_SUIVI));
  }, []);

  /* LIA AU TRAVAIL : une question a @Lia sans reponse tient une bulle en bas du fil (`liaEnAttente`). */
  const suivisLia = useSuivisLia(fil ? port : null, conversationId);
  const lia = useLiaEnAttente(fil ? messages : null, suivisLia, false);

  if (ancre === null || cle === null) return null;
  const raison = port === null ? RAISON_SOCLE_ABSENT : courant?.raison ?? (fil ? erreurDuFil : null);
  const base: SuiviDuGabarit = {
    compte: courant?.compte ?? null,
    label: libelleDuSuivi(ancre),
    ...(raison === null ? {} : { raison }),
    onOuvrir: () => ouvrirLeCentreSurIci(ancre),
  };
  if (!fil || port === null) return base;
  /* Pas encore de conversation : le fil est vide, pas en chargement. */
  const enAttente = courant === null || (conversationId !== null && misEnMots === null);
  const reste = messages !== null && messages.length >= limite && limite < FENETRE_DU_SUIVI_MAX;
  return {
    ...base,
    entrees,
    /* Un message envoye ne attend pas le premier rendu du fil : il se montre. */
    chargement: enAttente && raison === null && envoisDeLaFiche.length === 0,
    ...(reste ? { plusAnciennes } : {}),
    contributeurs: contributeursDuSuivi(misEnMots?.entrees ?? []),
    activites: epingles,
    ...(lia.attentes.length > 0 ? { lia: { attentes: lia.attentes, maintenant: lia.maintenant, signature: lia.signature } } : {}),
    detail,
    envoyer,
    renvoyer,
    abandonner,
    ...(peutEpingler && raison === null ? { epingle: { epinglee: suiviEpingle, basculer: basculerLEpingle } } : {}),
    marquerFaite,
    ...(options.lectureSeule === true ? { lectureSeule: true } : {}),
  };
}
